import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Progress,
  Spinner,
  Alert,
  Badge,
  UncontrolledTooltip
} from 'reactstrap';
import api from '../../api';
import './IVTraining.css'; // We'll create this file for animations

// Import the IV Dice image
import ivDiceImage from '../../assets/images/items/IVDice.png';

const IVTraining = ({ pokemonId, onIVsUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [ivData, setIvData] = useState(null);
  const [availableDice, setAvailableDice] = useState([]);
  const [applying, setApplying] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Stat colors for visual representation
  const statColors = {
    hp: '#FF5959',
    attack: '#F5AC78',
    defense: '#FAE078',
    special_attack: '#9DB7F5',
    special_defense: '#A7DB8D',
    speed: '#FA92B2'
  };

  // Map stat names to their display names
  const statDisplayNames = {
    hp: 'HP',
    attack: 'Attack',
    defense: 'Defense',
    special_attack: 'Sp. Atk',
    special_defense: 'Sp. Def',
    speed: 'Speed'
  };

  // Fetch Pokemon IVs and available dice
  useEffect(() => {
    const fetchIVData = async () => {
      try {
        setLoading(true);
        
        // Fetch Pokemon stats with IVs
        const statsResponse = await api.get(`/v1/pokemon/${pokemonId}/stats`, {
          withCredentials: true
        });
        
        // Fetch available IV dice
        const diceResponse = await api.get('/v1/training/available-iv-dice', {
          withCredentials: true
        });
        
        setIvData(statsResponse.data);
        setAvailableDice(diceResponse.data.dice_items || []);
      } catch (error) {
        console.error('Error fetching IV data:', error);
        setMessage({
          text: 'Failed to load IV data. Please try again.',
          type: 'danger'
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (pokemonId) {
      fetchIVData();
    }
  }, [pokemonId]);

  // Create dice animation
  const createDiceAnimation = (buttonElement) => {
    if (!buttonElement) return;
    
    // Get button position
    const buttonRect = buttonElement.getBoundingClientRect();
    const parentElement = buttonElement.closest('.tab-pane');
    if (!parentElement) return;
    
    // Create effect container
    const effectContainer = document.createElement('div');
    effectContainer.className = 'dice-effect-container';
    parentElement.style.position = 'relative';
    parentElement.appendChild(effectContainer);
    
    // Create dice image animation
    const diceImage = document.createElement('img');
    diceImage.className = 'dice-image-animation';
    diceImage.src = ivDiceImage;
    diceImage.alt = 'IV Dice';
    
    // Position at button location
    const parentRect = parentElement.getBoundingClientRect();
    diceImage.style.left = `${buttonRect.left - parentRect.left + (buttonRect.width / 2) - 16}px`;
    diceImage.style.top = `${buttonRect.top - parentRect.top + (buttonRect.height / 2) - 16}px`;
    
    effectContainer.appendChild(diceImage);
    
    // Create sparkle effects
    for (let i = 0; i < 5; i++) {
      const sparkle = document.createElement('div');
      sparkle.className = 'dice-sparkle';
      
      // Position around the dice
      sparkle.style.left = `${buttonRect.left - parentRect.left + (buttonRect.width / 2) - 6 + (Math.random() * 20 - 10)}px`;
      sparkle.style.top = `${buttonRect.top - parentRect.top + (buttonRect.height / 2) - 6 + (Math.random() * 20 - 10)}px`;
      
      // Random delay
      sparkle.style.animationDelay = `${Math.random() * 0.3}s`;
      
      effectContainer.appendChild(sparkle);
    }
    
    // Remove container after animations complete
    setTimeout(() => {
      if (effectContainer && effectContainer.parentNode) {
        effectContainer.parentNode.removeChild(effectContainer);
      }
    }, 2000);
  };

  // Create animation for the stat that was rerolled
  const createStatAnimation = (stat, oldValue, newValue) => {
    const statElement = document.getElementById(`iv-bar-${stat}`);
    if (!statElement) return;
    
    // Create effect container
    const effectContainer = document.createElement('div');
    effectContainer.className = 'iv-stat-effect-container';
    statElement.parentNode.style.position = 'relative';
    statElement.parentNode.appendChild(effectContainer);
    
    // Create value change display
    const valueChange = document.createElement('div');
    valueChange.className = 'iv-value-change';
    
    // Determine if value increased or decreased
    if (newValue > oldValue) {
      valueChange.classList.add('increase');
      valueChange.textContent = `+${newValue - oldValue}`;
    } else if (newValue < oldValue) {
      valueChange.classList.add('decrease');
      valueChange.textContent = `${newValue - oldValue}`;
    } else {
      valueChange.textContent = '±0';
    }
    
    // Position near the stat bar
    const statRect = statElement.getBoundingClientRect();
    const parentRect = statElement.parentNode.getBoundingClientRect();
    valueChange.style.left = `${statRect.right - parentRect.left + 10}px`;
    valueChange.style.top = `${statRect.top - parentRect.top}px`;
    
    effectContainer.appendChild(valueChange);
    
    // Create particles around the stat bar
    for (let i = 0; i < 10; i++) {
      const particle = document.createElement('div');
      particle.className = 'iv-stat-particle';
      
      // Set color based on stat
      particle.style.backgroundColor = statColors[stat];
      particle.style.boxShadow = `0 0 5px ${statColors[stat]}`;
      
      // Position around the stat bar
      particle.style.left = `${statRect.left - parentRect.left + (Math.random() * statRect.width)}px`;
      particle.style.top = `${statRect.top - parentRect.top + (Math.random() * statRect.height)}px`;
      
      // Random delay
      particle.style.animationDelay = `${Math.random() * 0.5}s`;
      
      effectContainer.appendChild(particle);
    }
    
    // Remove container after animations complete
    setTimeout(() => {
      if (effectContainer && effectContainer.parentNode) {
        effectContainer.parentNode.removeChild(effectContainer);
      }
    }, 2000);
  };

  // Apply IV dice to Pokemon
  const applyIVDice = async (diceId, event) => {
    if (!pokemonId || applying) return;
    
    setApplying(true);
    setMessage({ text: '', type: '' });
    
    // Get the button element that was clicked
    const buttonElement = event.currentTarget;
    
    try {
      // Start dice animation immediately for better UX
      createDiceAnimation(buttonElement);
      
      const { data } = await api.post('/v1/training/use-iv-dice', {
        dice_id: diceId,
        pokemon_id: pokemonId
      }, {
        withCredentials: true
      });
      
      // Get the stat that was updated and values
      const updatedStat = data.updated_stat.toLowerCase().replace(' ', '_');
      const oldValue = data.old_value;
      const newValue = data.new_value;
      
      // Create animation for the stat change
      createStatAnimation(updatedStat, oldValue, newValue);
      
      // Update local IV data
      setIvData({
        ...ivData,
        ivs: data.all_ivs,
        stats: data.stats
      });
      
      // Update available dice (decrease count)
      setAvailableDice(prev => 
        prev.map(dice => 
          dice.id === diceId 
            ? { ...dice, amount: dice.amount - 1 }
            : dice
        ).filter(dice => dice.amount > 0)
      );
      
      setMessage({
        text: data.message,
        type: 'success'
      });
      
      // Notify parent component if needed
      if (onIVsUpdated) {
        onIVsUpdated(data);
      }
    } catch (error) {
      console.error('Error applying IV dice:', error);
      setMessage({
        text: error.response?.data?.detail || 'Failed to apply IV dice. Please try again.',
        type: 'danger'
      });
    } finally {
      setTimeout(() => {
        setApplying(false);
      }, 2000); // Give animation time to play
    }
  };

  if (loading) {
    return (
      <Card className="shadow-sm" style={{ background: '#272d39', borderRadius: '12px' }}>
        <CardBody className="text-center">
          <Spinner color="light" />
          <p className="mt-2 text-light">Loading IV training data...</p>
        </CardBody>
      </Card>
    );
  }

  if (!ivData) {
    return (
      <Card className="shadow-sm" style={{ background: '#272d39', borderRadius: '12px' }}>
        <CardBody className="text-center">
          <p className="text-light">No IV data available for this Pokémon.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm" style={{ background: '#272d39', borderRadius: '12px' }}>
      <CardBody>
        <CardTitle tag="h5" className="text-white mb-3">
          IV Training
        </CardTitle>
        
        {message.text && (
          <Alert color={message.type} className="mb-3">
            {message.text}
          </Alert>
        )}
        
        <Row className="mb-4">
          {Object.entries(ivData.ivs || {}).map(([stat, value]) => (
            <Col md="6" key={stat} className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="text-white">{statDisplayNames[stat]}</span>
                <span className="text-white">{value}/31</span>
              </div>
              <Progress
                id={`iv-bar-${stat}`}
                value={(value / 31) * 100}
                style={{ 
                  height: '10px', 
                  backgroundColor: '#3a4052',
                  borderRadius: '5px'
                }}
                color={statColors[stat].replace('#', '')}
              />
            </Col>
          ))}
        </Row>
        
        <div className="mb-3">
          <h6 className="text-white mb-2">Available IV Dice</h6>
          {availableDice.length > 0 ? (
            <Row>
              {availableDice.map((dice) => (
                <Col xs="6" md="4" key={dice.id} className="mb-2">
                  <Button
                    color="dark"
                    outline
                    className="w-100 d-flex align-items-center justify-content-between"
                    onClick={(e) => applyIVDice(dice.id, e)}
                    disabled={applying}
                    id={`dice-${dice.id}`}
                  >
                    <img 
                      src={ivDiceImage} 
                      alt={dice.name} 
                      style={{ width: '24px', height: '24px', marginRight: '8px' }} 
                    />
                    <span>{dice.name}</span>
                    <Badge color="info" pill>
                      {dice.amount}
                    </Badge>
                  </Button>
                  <UncontrolledTooltip target={`dice-${dice.id}`}>
                    {dice.description}
                  </UncontrolledTooltip>
                </Col>
              ))}
            </Row>
          ) : (
            <p className="text-muted">No IV dice available. You can get them from the shop.</p>
          )}
        </div>
        
        <div className="mt-3">
          <small className="text-muted">
            IV Dice randomly rerolls one of your Pokémon's IV stats.
          </small>
        </div>
      </CardBody>
    </Card>
  );
};

export default IVTraining;
